// client/src/utils/validateUnitSize.ts
//
// The Unit Size cell's pre-commit check on the vendor invoice review grid.
//
// The merged Unit Size column writes the pack unit too, so it can only accept a
// value it can actually split: a number and a unit we know.
//
// MIRRORED FILE: src/utils/validateUnitSize.js is a line-for-line twin. CRA's
// ModuleScopePlugin forbids importing from src/, so the copy is the only
// option (the repo already does this for several utils). Change one, change
// both. Registered as BUSINESS_RULES#unit-size-cell-format — the server twin
// exists and is parity-tested (`mirroredUtilParity.test.js`), but as of
// 2026-09-24 nothing on the server CALLS it yet; see the registry row for the
// call site it belongs at (`patchVendorInvoice` in `vendorInvoice.service.js`,
// the save-time boundary this file itself guards in the browser) and for why
// a Confirm & sync gate was tried first and reverted.
//
// Moved here from `features/vendorInvoices/` on 2026-09-24 so it sits beside
// its server twin the way every other mirrored pair does (invariant 2:
// `src/utils/X.js` <-> `client/src/utils/X.ts`); `mirroredUtilParity.test.js`
// is the enforcement.
import { canonicalUnit, unitFromSizeText } from "./unitVocabulary";

export function validateUnitSize(value: any): string | null {
  const v = String(value ?? "").trim();
  if (!v) return null;
  if (!/\d/.test(v)) return 'Enter the size as a number and a unit, e.g. "500 ML"';
  const raw = unitFromSizeText(v);
  if (!raw) return 'Add the unit, e.g. "500 ML"';
  if (canonicalUnit(raw)) return null;
  // USC-BUG-0052 (QA): a bare "M" is deliberately not a unit platform-wide
  // (BUSINESS_RULES#unit-vocabulary — OCR noise, and a garment size), but a
  // reviewer typing "50m" means metres, so say how metres are written.
  const amount = v.slice(0, v.length - raw.length).trim();
  if (raw === "M") return `For metres write MTR, e.g. "${amount} MTR"`;
  return `"${raw}" is not a unit we know — use ML, L, G, KG, OZ, LBS, GAL, PCS or MTR`;
}
